import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { formatRemoteVncRouteDeviceId } from '../features/remoteVnc/utils/buildRemoteVncWebSocketUrl';
import watchdogConfigService from '../services/watchdogConfigService';
import type {
  NewAgentRow,
  WatchdogConfigPayload,
  WatchdogDesiredPackage,
  WatchdogSelfUpdateInstruction,
} from '../types/features/newAgents';
import {
  adminButtonSx,
  adminButtonToneSx,
  adminCombineSx,
  adminPageHeaderSx,
} from '../styles/adminComponentStyles';

const refreshButtonSx = adminCombineSx(adminButtonSx, adminButtonToneSx.secondary, {
  height: 32,
  minHeight: 32,
  minWidth: 92,
  px: 1.5,
  py: 0,
  '& .MuiButton-startIcon': {
    ml: 0,
    mr: 0.75,
  },
  '& .MuiButton-startIcon .MuiSvgIcon-root': {
    fontSize: 18,
  },
});

interface WatchdogConfigFormState {
  wifi_mac_address: string;
  store_id: string;
  device_label: string;
  enabled: boolean;
  foreground_package_name: string;
  poll_interval_seconds: string;
  reboot_cooldown_minutes: string;
  packages_json: string;
  watchdog_update_json: string;
}

const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return 'Never';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const formatWatchdogVersion = (agent: NewAgentRow): string => {
  if (agent.last_seen_watchdog_version_name && agent.last_seen_watchdog_version_code != null) {
    return `${agent.last_seen_watchdog_version_name} (${agent.last_seen_watchdog_version_code})`;
  }
  if (agent.last_seen_watchdog_version_name) {
    return agent.last_seen_watchdog_version_name;
  }
  if (agent.last_seen_watchdog_version_code != null) {
    return String(agent.last_seen_watchdog_version_code);
  }
  return 'Unknown';
};

const isRegisteredAgent = (agent: NewAgentRow): boolean => Boolean(agent.store_id);

const buildAgentConsoleUrl = (agent: NewAgentRow): string | null => {
  const wifiMacAddress = agent.wifi_mac_address?.trim();
  if (!wifiMacAddress) {
    return null;
  }

  const consoleId = formatRemoteVncRouteDeviceId(wifiMacAddress);
  const url = new URL(`/remote-vnc/${encodeURIComponent(consoleId)}`, window.location.origin);
  return url.toString();
};

const buildFormState = (agent: NewAgentRow): WatchdogConfigFormState => ({
  wifi_mac_address: agent.wifi_mac_address,
  store_id: agent.store_id || '',
  device_label: agent.device_label || '',
  enabled: agent.enabled,
  foreground_package_name: agent.foreground_package_name || '',
  poll_interval_seconds: String(agent.poll_interval_seconds),
  reboot_cooldown_minutes: String(agent.reboot_cooldown_minutes),
  packages_json: JSON.stringify(agent.packages || [], null, 2),
  watchdog_update_json: agent.watchdog_update ? JSON.stringify(agent.watchdog_update, null, 2) : '',
});

const parsePackagesJson = (value: string): WatchdogDesiredPackage[] => {
  if (!value.trim()) {
    return [];
  }
  const parsed = JSON.parse(value);
  if (!Array.isArray(parsed)) {
    throw new Error('Packages JSON must be an array.');
  }
  return parsed as WatchdogDesiredPackage[];
};

const parseWatchdogUpdateJson = (value: string): WatchdogSelfUpdateInstruction | null => {
  if (!value.trim()) {
    return null;
  }
  const parsed = JSON.parse(value);
  if (Array.isArray(parsed) || typeof parsed !== 'object' || parsed === null) {
    throw new Error('Watchdog update JSON must be an object.');
  }
  return parsed as WatchdogSelfUpdateInstruction;
};

const NewAgents: React.FC = () => {
  const [agents, setAgents] = useState<NewAgentRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [loadingSelection, setLoadingSelection] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [showRegistered, setShowRegistered] = useState<boolean>(false);
  const [selectedMac, setSelectedMac] = useState<string>('');
  const selectedMacRef = useRef<string>('');
  const [selectedAgent, setSelectedAgent] = useState<NewAgentRow | null>(null);
  const [formState, setFormState] = useState<WatchdogConfigFormState | null>(null);
  const [error, setError] = useState<string>('');
  const [saveMessage, setSaveMessage] = useState<string>('');

  const selectedAgentConsoleUrl = selectedAgent ? buildAgentConsoleUrl(selectedAgent) : null;

  const loadAgentConfig = useCallback(async (wifiMacAddress: string) => {
    try {
      setLoadingSelection(true);
      const response = await watchdogConfigService.getConfig(wifiMacAddress);
      selectedMacRef.current = response.wifi_mac_address;
      setSelectedMac(response.wifi_mac_address);
      setSelectedAgent(response);
      setFormState(buildFormState(response));
    } catch (_error: unknown) {
      setError('Failed to load watchdog configuration for the selected agent.');
    } finally {
      setLoadingSelection(false);
    }
  }, []);

  const loadAgents = useCallback(async (showRefreshing: boolean, preferredMac?: string) => {
    try {
      if (showRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError('');
      setSaveMessage('');
      const response = await watchdogConfigService.getConfigs(showRegistered);
      setAgents(response);

      const nextSelectedMac = preferredMac && response.some((agent) => agent.wifi_mac_address === preferredMac)
        ? preferredMac
        : response[0]?.wifi_mac_address || '';

      if (nextSelectedMac) {
        await loadAgentConfig(nextSelectedMac);
      } else {
        selectedMacRef.current = '';
        setSelectedMac('');
        setSelectedAgent(null);
        setFormState(null);
      }
    } catch (_error: unknown) {
      setError('Failed to load watchdog agents. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [loadAgentConfig, showRegistered]);

  useEffect(() => {
    void loadAgents(false, selectedMacRef.current);
  }, [loadAgents, showRegistered]);

  const handleSelectAgent = useCallback((wifiMacAddress: string) => {
    setSaveMessage('');
    void loadAgentConfig(wifiMacAddress);
  }, [loadAgentConfig]);

  const updateField = useCallback(<K extends keyof WatchdogConfigFormState>(key: K, value: WatchdogConfigFormState[K]) => {
    setFormState((previous) => (previous ? { ...previous, [key]: value } : previous));
  }, []);

  const buildPayload = useCallback((): WatchdogConfigPayload => {
    if (!formState) {
      throw new Error('No watchdog config selected.');
    }

    const pollIntervalSeconds = Number.parseInt(formState.poll_interval_seconds, 10);
    const rebootCooldownMinutes = Number.parseInt(formState.reboot_cooldown_minutes, 10);
    if (!Number.isFinite(pollIntervalSeconds) || pollIntervalSeconds < 1) {
      throw new Error('Poll interval must be a positive number.');
    }
    if (!Number.isFinite(rebootCooldownMinutes) || rebootCooldownMinutes < 1) {
      throw new Error('Reboot cooldown must be a positive number.');
    }

    return {
      wifi_mac_address: formState.wifi_mac_address,
      store_id: formState.store_id.trim() || null,
      device_label: formState.device_label.trim() || null,
      enabled: formState.enabled,
      foreground_package_name: formState.foreground_package_name.trim() || null,
      packages: parsePackagesJson(formState.packages_json),
      watchdog_update: parseWatchdogUpdateJson(formState.watchdog_update_json),
      poll_interval_seconds: pollIntervalSeconds,
      reboot_cooldown_minutes: rebootCooldownMinutes,
    };
  }, [formState]);

  const handleSave = useCallback(async () => {
    if (!formState) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      const payload = buildPayload();
      const response = await watchdogConfigService.updateConfig(formState.wifi_mac_address, payload);
      setSelectedAgent(response);
      setFormState(buildFormState(response));
      setSaveMessage(`Saved watchdog config for ${response.wifi_mac_address}.`);
      await loadAgents(true, response.wifi_mac_address);
      if (!showRegistered && isRegisteredAgent(response)) {
        setSaveMessage(
          `Saved watchdog config for ${response.wifi_mac_address}. It is now registered, so enable "Show registered watchdogs" to keep editing it here.`,
        );
      }
    } catch (saveError: unknown) {
      const message = saveError instanceof Error ? saveError.message : 'Failed to save watchdog config.';
      setError(message);
    } finally {
      setSaving(false);
    }
  }, [buildPayload, formState, loadAgents, showRegistered]);

  return (
    <Box >
      <Box sx={adminPageHeaderSx}>
        <Box>
          <Typography
            className="AdminPageHeader-title"
            sx={{ mb: '4px', fontSize: '20px !important' }}
          >
            New Agents
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: '#4B5563', fontSize: 13, fontWeight: 400, lineHeight: 1.4 }}
          >
            Review pending watchdogs, enable registered ones in the list, and update their config from one screen.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <FormControlLabel
            control={(
              <Checkbox
                checked={showRegistered}
                onChange={(event) => setShowRegistered(event.target.checked)}
              />
            )}
            label="Show registered watchdogs"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: 14 } }}
          />
          <Button
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
            size="small"
            onClick={() => void loadAgents(true, selectedMac)}
            disabled={loading || refreshing || saving}
            sx={refreshButtonSx}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {saveMessage ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {saveMessage}
        </Alert>
      ) : null}

      <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3}>
        <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 3, width: { xs: '100%', xl: 430 }, overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, py: 2, background: '#F8FBFF' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '16px', lineHeight: 1.2, }}>
              Watchdogs
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, fontSize:'13px' }}>
              Click a watchdog to edit its server-side configuration.
            </Typography>
          </Box>
          <Divider />
          {loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
              <CircularProgress />
            </Stack>
          ) : agents.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 8, px: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                No watchdogs found
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                Devices will appear here after they first contact the watchdog endpoint.
              </Typography>
            </Stack>
          ) : (
            <TableContainer sx={{ maxHeight: 760 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Wi-Fi MAC</TableCell>
                    <TableCell>Last Seen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agents.map((agent) => {
                    const registered = isRegisteredAgent(agent);
                    const isSelected = agent.wifi_mac_address === selectedMac;
                    return (
                      <TableRow
                        key={agent.id}
                        hover
                        selected={isSelected}
                        onClick={() => handleSelectAgent(agent.wifi_mac_address)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Chip
                            label={registered ? 'Registered' : 'Pending'}
                            size="small"
                            color={registered ? 'success' : 'warning'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          {agent.wifi_mac_address}
                        </TableCell>
                        <TableCell>{formatDateTime(agent.last_seen_at)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 3, flex: 1, overflow: 'hidden' }}>
          <Box sx={{ px: 2.5, py: 2, background: '#F8FBFF' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '16px', lineHeight: 1.2, }}>
              Watchdog Configuration
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, fontSize:'13px' }}>
              Basic fields are editable directly. Managed packages and self-update stay in JSON for now.
            </Typography>
          </Box>
          <Divider />
          {!formState || !selectedAgent ? (
            <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 10, px: 3 }}>
              {loadingSelection ? <CircularProgress /> : null}
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Select a watchdog from the list to edit its configuration.
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ p: 3 }}>
              {loadingSelection ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Loading selected watchdog...
                  </Typography>
                </Stack>
              ) : null}

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Wi-Fi MAC"
                  value={formState.wifi_mac_address}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Device Label"
                  value={formState.device_label}
                  onChange={(event) => updateField('device_label', event.target.value)}
                  fullWidth
                />
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Store ID"
                  value={formState.store_id}
                  onChange={(event) => updateField('store_id', event.target.value)}
                  fullWidth
                />
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Foreground Package"
                  value={formState.foreground_package_name}
                  onChange={(event) => updateField('foreground_package_name', event.target.value)}
                  fullWidth
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={formState.enabled}
                      onChange={(event) => updateField('enabled', event.target.checked)}
                    />
                  )}
                  label="Enabled"
                  sx={{ minWidth: 180, ml: 0.5 }}
                />
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Poll Interval (seconds)"
                  value={formState.poll_interval_seconds}
                  onChange={(event) => updateField('poll_interval_seconds', event.target.value)}
                  fullWidth
                />
                <TextField
                  label="Reboot Cooldown (minutes)"
                  value={formState.reboot_cooldown_minutes}
                  onChange={(event) => updateField('reboot_cooldown_minutes', event.target.value)}
                  fullWidth
                />
              </Stack>

              <TextField
                label="Managed Packages JSON"
                value={formState.packages_json}
                onChange={(event) => updateField('packages_json', event.target.value)}
                multiline
                minRows={10}
                fullWidth
              />

              <TextField
                label="Watchdog Self-Update JSON"
                value={formState.watchdog_update_json}
                onChange={(event) => updateField('watchdog_update_json', event.target.value)}
                multiline
                minRows={5}
                fullWidth
                helperText="Leave blank when no watchdog self-update should be pushed."
              />

              <Divider />

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
                <Stack spacing={0.5}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Last Seen: {formatDateTime(selectedAgent.last_seen_at)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Last Seen IP: {selectedAgent.last_seen_ip || 'Unknown'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Watchdog Version: {formatWatchdogVersion(selectedAgent)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Reported Packages: {selectedAgent.last_seen_reported_packages.length}
                  </Typography>
                </Stack>

                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={() => void handleSave()}
                  disabled={saving || loadingSelection}
                >
                  Save Configuration
                </Button>
              </Stack>

              <Divider />

              <Box
                sx={{
                  border: '1px solid #E5E7EB',
                  borderRadius: 2,
                  p: 2,
                  backgroundColor: '#F9FAFB',
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Agent Console
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                      Launch the selected device&apos;s Remote VNC session in a new browser tab.
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                      Console URL: {selectedAgentConsoleUrl || 'Unavailable until the watchdog reports a Wi-Fi MAC address.'}
                    </Typography>
                  </Box>

                  <Button
                    variant="outlined"
                    startIcon={<OpenInNewIcon />}
                    component="a"
                    href={selectedAgentConsoleUrl || undefined}
                    target="_blank"
                    rel="noreferrer"
                    disabled={!selectedAgentConsoleUrl}
                  >
                    Launch Remote VNC
                  </Button>
                </Stack>
              </Box>
            </Stack>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};

export default NewAgents;
